import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, RefreshCw, ChevronDown, ChevronRight,
  Calendar, Loader2, PlayCircle, Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { MarketFlag } from '../lib/MarketFlag';

const STATUS_LABELS = {
  running:   'Kör',
  completed: 'Klar',
  failed:    'Misslyckad',
};

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDuration(startedAt, completedAt) {
  if (!startedAt) return '—';
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffSec = Math.round((end - new Date(startedAt)) / 1000);
  if (diffSec < 0) return '—';
  if (diffSec < 60) return `${diffSec}s`;
  const m = Math.floor(diffSec / 60);
  const s = diffSec % 60;
  return `${m}m ${s}s`;
}

function RunDetail({ runId, userId }) {
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase || !userId) { setLoading(false); return; }
    setLoading(true);
    supabase
      .from('pipeline_queue')
      .select('id,part_no,product_name,status,markets,published_markets')
      .eq('user_id', userId)
      .eq('run_id', runId)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { toast.error(`Kunde inte hämta produkter: ${error.message}`); return; }
        setProducts(data || []);
      });
  }, [runId, userId]);

  if (loading) {
    return (
      <div className="ap-history-detail" data-testid={`ap-history-detail-${runId}`}>
        <Loader2 size={14} className="ap-progress-spin" />
      </div>
    );
  }

  return (
    <div className="ap-history-detail" data-testid={`ap-history-detail-${runId}`}>
      <div className="ap-history-detail-title">
        Produkter i körningen ({products.length})
      </div>
      {products.length === 0 ? (
        <div style={{ fontSize: 12, color: '#64748b' }}>Inga produkter registrerade för denna körning.</div>
      ) : (
        <div className="ap-history-products">
          {products.map(p => {
            const pubCount = (p.published_markets || []).length;
            const totalMkts = (p.markets || []).length;
            return (
              <div key={p.id} className="ap-history-product-row" data-testid={`ap-history-product-${p.id}`}>
                <div>
                  <code>{p.part_no}</code>
                  <strong style={{ marginLeft: 8 }}>{p.product_name || '—'}</strong>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {pubCount}/{totalMkts} publicerade
                  </span>
                  <span className={`ap-queue-status ap-queue-status--${p.status}`}>
                    {p.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function HistoryTab() {
  const { user } = useAuth() || {};
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadRuns = useCallback(async () => {
    if (!user || !supabase) { setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('pipeline_runs')
      .select('*')
      .eq('user_id', user.id)
      .order('started_at', { ascending: false })
      .limit(100);
    setLoading(false);
    if (error) {
      toast.error(`Kunde inte hämta historik: ${error.message}`);
      return;
    }
    setRuns(data || []);
  }, [user]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  if (loading) {
    return (
      <div className="ap-queue-empty" data-testid="ap-history-loading">
        <Loader2 size={20} className="ap-progress-spin" style={{ marginBottom: 8 }} />
        <div>Hämtar historik...</div>
      </div>
    );
  }

  return (
    <div data-testid="ap-history-tab">
      <div className="ap-queue-toolbar">
        <div style={{ fontSize: 12.5, color: '#64748b' }}>
          {runs.length} körning{runs.length !== 1 ? 'ar' : ''} — senaste 100 visas
        </div>
        <div className="ap-queue-spacer" />
        <button className="ap-queue-refresh" onClick={loadRuns} title="Uppdatera" data-testid="ap-history-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="ap-queue-empty" data-testid="ap-history-empty">
          <Calendar size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
          <div>Inga körningar ännu</div>
        </div>
      ) : (
        <div className="ap-history-table-wrap">
          <table className="ap-history-table">
            <thead>
              <tr>
                <th style={{ width: 18 }}></th>
                <th>Startad</th>
                <th>Typ</th>
                <th>Marknader</th>
                <th style={{ textAlign: 'right' }}>Hittade</th>
                <th style={{ textAlign: 'right' }}>Genererade</th>
                <th style={{ textAlign: 'right' }}>Misslyckade</th>
                <th>Status</th>
                <th>Varaktighet</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => {
                const expanded = expandedId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={expanded ? 'ap-queue-row--expanded' : ''}
                      onClick={() => setExpandedId(expanded ? null : r.id)}
                      data-testid={`ap-history-row-${r.id}`}
                    >
                      <td>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td style={{ fontSize: 12 }}>
                        <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                        {formatDate(r.started_at)}
                      </td>
                      <td>
                        <span className="ap-mkt-chip">
                          {r.triggered_by === 'schedule' ? <><Bot size={10} /> Schemalagd</> : <><PlayCircle size={10} /> Manuell</>}
                        </span>
                      </td>
                      <td>
                        <div className="ap-mkt-chips">
                          {(r.markets || []).map(m => (
                            <span key={m} className="ap-mkt-chip">
                              <MarketFlag market={m} /> {m}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.products_found ?? 0}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {r.products_generated ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', color: r.products_failed ? '#b91c1c' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                        {r.products_failed ?? 0}
                      </td>
                      <td>
                        <span className={`ap-run-status ap-run-status--${r.status}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {formatDuration(r.started_at, r.completed_at)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, background: '#f8fafc' }}>
                          <RunDetail runId={r.id} userId={user.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
